/* eslint-disable @typescript-eslint/no-empty-function */

import React, { useCallback, useRef, useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputLabel,
  Button as MaterialButton,
} from '@material-ui/core';

import { makeStyles } from '@mui/styles';

import Box from '@material-ui/core/Box';
import { Form } from '@unform/web';
import { Toolbar, Data } from 'react-data-grid-addons';
import { FiEdit, FiPlusCircle, FiTrash2 } from 'react-icons/fi';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';
import DataGrid from 'react-data-grid';
import { Container, Info, Tabela } from './styles';
import NavBar from '../../../components/NavBar';
import Header from '../../../components/Header';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';
import getValidationErrors from '../../../utils/getValidationErrors';
import Input from '../../../components/Input';

export interface LocalProducao {
  nm: string;
  hash: string;
  cd_empresa: string;
}

const useStyles = makeStyles({
  root: {
    flexGrow: 1,
    backgroundColor: '#FFF',
    height: '60vh',
  },
});

const defaultColumnProperties = {
  resizable: true,
};

const selectors = Data.Selectors;

const columns = [
  {
    key: 'nm',
    name: 'Nome',
    filterable: true,
  },
  {
    key: 'acoes',
    name: 'Ações',
    filterable: false,
    width: 80,
  },
].map((c) => ({ ...c, ...defaultColumnProperties }));

const handleFilterChange = (filter: any) => (filters: any) => {
  const newFilters = { ...filters };
  if (filter.filterTerm) {
    newFilters[filter.column.key] = filter;
  } else {
    delete newFilters[filter.column.key];
  }
  return newFilters;
};

function getRows(rows: any, filters: any) {
  return selectors.getRows({ rows, filters });
}
const EmptyRowsView = () => {
  const message = 'Ops, não há dados disponíveis...';
  return (
    <div style={{ textAlign: 'center', padding: '100px' }}>
      <h3>{message}</h3>
    </div>
  );
};

const LocalProducao: React.FC = () => {
  const formRef = useRef<FormHandles>(null);

  const classes = useStyles();
  const [cadastrar, setCadastrar] = useState(true);

  const { addToast } = useToast();

  const [disabled, setDisabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [localProducaoDefault, setLocalProducaoDefault] = useState<
    LocalProducao
  >({
    nm: '',
  } as LocalProducao);

  const [listaLocaisProducao, setListaLocaisProducao] = useState([] as any);

  const [filters, setFilters] = useState({});

  const filteredRows = getRows(listaLocaisProducao, filters);
  const handleClickOpen = useCallback(() => {
    const p = {
      nm: '',
    } as LocalProducao;
    setLocalProducaoDefault(p);
    setCadastrar(true);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleCloseConfirmation = useCallback(() => {
    setOpenConfirmation(false);
  }, []);
  const populaTabela = useCallback(() => {
    api
      .get<LocalProducao[]>('locais_producao')
      .then((response) => {
        setListaLocaisProducao(response.data);
      })
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: `Ops, ocorreu algum erro ao listar os locais de produção: ${error}`,
        });
      });
  }, [addToast]);
  const deletaLocalProducao = useCallback(() => {
    setDisabled(true);
    api
      .delete(`/locais_producao/${localProducaoDefault.nm}`)
      .then(() => {
        addToast({
          type: 'success',
          title: 'Sucesso',
          description: `O local de produção '${localProducaoDefault.nm}' foi deletado com sucesso.`,
        });
        populaTabela();
        handleCloseConfirmation();
        setDisabled(false);
      })
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: `Ocorreu um erro ao tentar deletar o local de produção '${localProducaoDefault.nm}': '${error}'.`,
        });
        handleCloseConfirmation();
        setDisabled(false);
      });
  }, [addToast, localProducaoDefault, handleCloseConfirmation, populaTabela]);

  const handleSubmit = useCallback(
    async (data: LocalProducao) => {
      setDisabled(true);

      try {
        const schema = Yup.object().shape({
          nm: Yup.string().required('Campo obrigatório'),
        });
        formRef.current?.setErrors({});

        await schema.validate(data, {
          abortEarly: false,
        });
        if (cadastrar) {
          const response = await api.post('locais_producao', data);

          if (response.status === 200) {
            populaTabela();
            handleClose();
            addToast({
              type: 'success',
              title: 'Sucesso',
              description: `O local de produção ${data.nm} foi cadastrada com sucesso.`,
            });
            setDisabled(false);
          }
        } else {
          // Editar classe selecionada
          console.log(data, localProducaoDefault);
          const response = await api.put('locais_producao', {
            ...data,
            nm_old: localProducaoDefault.nm,
          });
          if (response.status === 200) {
            populaTabela();
            handleClose();
            addToast({
              type: 'success',
              title: 'Sucesso',
              description: `O local de producao ${data.nm} foi editada com sucesso.`,
            });
            setDisabled(false);
          }
        }
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);

          setDisabled(false);
          return;
        }
        handleClose();
        addToast({
          type: 'error',
          title: 'Erro',
          description: `Ocorreu um erro ao ${
            cadastrar ? 'cadastrar' : 'editar'
          } o local de produção. Verifique se já existe um local de produção com esse nome.`,
        });
        setDisabled(false);
      }
    },
    [localProducaoDefault],
  );
  const getCellActions = useCallback(
    (column: any, row: any) => {
      const cellActions: { [index: string]: any } = {
        acoes: [
          {
            icon: (
              <span className="remover">
                <FiTrash2 />
              </span>
            ),
            callback: () => {
              setLocalProducaoDefault({
                nm: row.nm,
              } as LocalProducao);
              setOpenConfirmation(true);
            },
          },
          {
            icon: (
              <span className="edit">
                <FiEdit />
              </span>
            ),
            callback: () => {
              setLocalProducaoDefault({
                nm: row.nm,
              } as LocalProducao);
              setCadastrar(false);
              setOpen(true);
            },
          },
        ],
      };
      return cellActions[column.key];
    },
    [localProducaoDefault],
  );
  useEffect(() => {
    populaTabela();
  }, [populaTabela]);
  return (
    <Container>
      <NavBar />
      <div id="page-wrap">
        <Header />
        <Info>
          <div className="header">
            <h1>LOCAIS DE PRODUÇÃO</h1>
            <button type="button" onClick={handleClickOpen}>
              <FiPlusCircle size={24} />
              CADASTRAR
            </button>
          </div>
          <Tabela>
            <DataGrid
              columns={columns}
              rowGetter={(i) => filteredRows[i]}
              rowsCount={filteredRows.length}
              minHeight={400}
              toolbar={<Toolbar enableFilter filterRowsButtonText="Filtrar" />}
              onAddFilter={(filter) => setFilters(handleFilterChange(filter))}
              onClearFilters={() => setFilters({})}
              getCellActions={getCellActions}
              emptyRowsView={EmptyRowsView}
            />
          </Tabela>

          <Dialog
            open={open}
            fullWidth
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogTitle id="responsive-dialog-title">
              {cadastrar ? 'Cadastrar' : 'Editar'} local de produção
            </DialogTitle>
            <DialogContent>
              <div className={classes.root}>
                <Form
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                  id="formulario"
                  ref={formRef}
                  onSubmit={handleSubmit}
                >
                  <Box flexWrap="wrap" component="div">
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      <div style={{ alignSelf: 'flex-start' }}>
                        <InputLabel
                          id="select-label"
                          style={{ marginBottom: '4px', marginLeft: '2px' }}
                        >
                          Nome da Local de Produção *
                        </InputLabel>
                        <Input
                          name="nm"
                          style={{ width: '250px' }}
                          placeholder="Nome do Local de Produção"
                          defaultValue={localProducaoDefault.nm}
                          autoFocus
                        />
                      </div>
                    </div>
                  </Box>
                </Form>
              </div>
            </DialogContent>
            <DialogActions>
              <MaterialButton onClick={handleClose} color="primary">
                Cancelar
              </MaterialButton>
              <MaterialButton
                type="submit"
                form="formulario"
                disabled={disabled}
                color="primary"
              >
                {cadastrar ? 'Cadastrar' : 'Editar'} local de produção
              </MaterialButton>
            </DialogActions>
          </Dialog>
          <Dialog
            open={openConfirmation}
            onClose={handleCloseConfirmation}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">Atenção!</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Tem certeza que deseja deletar o local de produção &apos;
                {localProducaoDefault.nm}&apos;?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <MaterialButton
                onClick={handleCloseConfirmation}
                style={{
                  color: '#2c73e9',
                }}
              >
                CANCELAR
              </MaterialButton>
              <MaterialButton
                onClick={deletaLocalProducao}
                disabled={disabled}
                style={{
                  color: '#2c73e9',
                }}
              >
                DELETAR
              </MaterialButton>
            </DialogActions>
          </Dialog>
        </Info>
      </div>
    </Container>
  );
};

export default LocalProducao;
